"use client";

import React from "react";
import SectionLayout from "../ui/SectionLayout";
import { SectionProps } from "./model";
import { getDefaultCmsSection } from "@/utils/helper";
import Link from "next/link";
import { motion } from "framer-motion";
import { CtaComponentProps } from "@/cms/models";
import CtaComponent from "../ui/CtaComponent";
import {
  mergeCtaSettings,
  mergeTypographySettings,
} from "@/utils/theme-helper";
import TypographyComponent, {
  TypographyComponentProps,
} from "../ui/TypographyComponent";

// contentPadding=sx(p-[4px]), sm(p-[8px], md(p-[16px]), lg(p-[32px]), xl(p-[48px]) (padding for hele contentområdet, altså det som kan indeholde upperContent, lowerContent og action) (default md size)
// contentGap=sx, sm, md, lg, xl (samme som oven over i px) default md

const ImageTextSectionComponent: React.FC<
  SectionProps<{
    upperContent: TypographyComponentProps;
    lowerContent: TypographyComponentProps;
    contentIcon: boolean;
    contentWidth: string;
    contentBgColor: string;
    contentTextColor: string;
    buttonContent?: string;
    url?: string;
    height: string;
    imageUrl: string;
    imageWidth: string;
    cta: CtaComponentProps;
    imagePosition: "left" | "right";
  }>
> = ({ cmsSection, index, cmsData }) => {
  const renderContent = () => (
    <div
      className={`flex section-${index} ${
        cmsSection.specifics.imagePosition == "left"
          ? "flex-col-reverse md:flex-row-reverse"
          : "flex-col-reverse md:flex-row"
      }`}
    >
      <div className="w-full md:w-[var(--content-width)] homepage-text-lg flex bg-[var(--imagetextsection-bg-color)] text-[var(--imagetextsection-text-color)]">
        <div className="flex flex-1 flex-col justify-center items-center gap-4 p-8 md:p-4 text-center">
          {cmsSection.specifics.upperContent && (
            <motion.div
              initial={{ opacity: 0, x: -100 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3 }}
              viewport={{ once: true }}
            >
              <TypographyComponent
                config={mergeTypographySettings(
                  cmsSection.specifics.upperContent,
                  cmsData,
                  {
                    size: "lg",
                    padding: "md",
                  }
                )}
              />
            </motion.div>
          )}
          {cmsSection.specifics.contentIcon && (
            <svg
              className=" size-10 text-[var(--imagetextsection-text-color)] transition duration-300 ease-in-out group-hover:text-[var(--imagetextsection-bg-color)]"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 512 512"
            >
              <path
                className="text-transparent group-hover:text-[var(--imagetextsection-text-color)]"
                fill="currentColor"
                opacity="1"
                d="M496 256A240 240 0 1 1 16 256a240 240 0 1 1 480 0zm-368 0c0 4.4 3.6 8 8 8l220.7 0-90.3 90.3c-3.1 3.1-3.1 8.2 0 11.3s8.2 3.1 11.3 0l104-104c3.1-3.1 3.1-8.2 0-11.3l-104-104c-3.1-3.1-8.2-3.1-11.3 0s-3.1 8.2 0 11.3L356.7 248 136 248c-4.4 0-8 3.6-8 8z"
              />
              <path
                fill="currentColor"
                d="M496 256A240 240 0 1 1 16 256a240 240 0 1 1 480 0zM0 256a256 256 0 1 0 512 0A256 256 0 1 0 0 256zM277.7 365.7l104-104c3.1-3.1 3.1-8.2 0-11.3l-104-104c-3.1-3.1-8.2-3.1-11.3 0s-3.1 8.2 0 11.3L356.7 248 136 248c-4.4 0-8 3.6-8 8s3.6 8 8 8l220.7 0-90.3 90.3c-3.1 3.1-3.1 8.2 0 11.3s8.2 3.1 11.3 0z"
              />
            </svg>
          )}
          {cmsSection.specifics.cta && (
            <div className="homepage-text-sm">
              <CtaComponent
                itemIndex={0}
                config={mergeCtaSettings(cmsSection.specifics.cta, cmsData)}
                id={`cta-component-${index}-0`}
              />
            </div>
          )}
          {cmsSection.specifics.lowerContent && (
            <motion.div
              initial={{ opacity: 0, x: -100 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3 }}
              viewport={{ once: true }}
            >
              <TypographyComponent
                config={mergeTypographySettings(
                  cmsSection.specifics.lowerContent,
                  cmsData,
                  {
                    size: "lg",
                    padding: "md",
                  }
                )}
              />
            </motion.div>
          )}
        </div>
      </div>
      <div
        style={{
          height: cmsSection.specifics.height,
        }}
        className="relative flex md:hidden w-full"
      >
        <img
          alt="Image"
          src={cmsSection.specifics.imageUrl}
          className="object-cover w-full h-full absolute inset-0"
        />
      </div>
      <div
        style={{
          height: cmsSection.specifics.height,
          width: "var(--image-width)",
        }}
        className="relative hidden md:flex w-full"
      >
        <img
          alt="Image"
          src={cmsSection.specifics.imageUrl}
          className="object-cover w-full h-full absolute inset-0"
        />
      </div>
    </div>
  );

  return (
    <SectionLayout
      cmsSection={getDefaultCmsSection(cmsSection, {
        width: "4xl",
        padding: "none",
      })}
    >
      <style>
        {`.section-${index}  {
          --imagetextsection-bg-color: ${cmsSection.specifics.contentBgColor};
          --imagetextsection-text-color: ${cmsSection.specifics.contentTextColor};
          --image-width: ${(parseFloat(cmsSection.specifics.imageWidth.split("/")[0]) / parseFloat(cmsSection.specifics.imageWidth.split("/")[1])) * 100}%;
          --content-width: ${(parseFloat(cmsSection.specifics.contentWidth.split("/")[0]) / parseFloat(cmsSection.specifics.contentWidth.split("/")[1])) * 100}%;
              }`}
      </style>
      {cmsSection.specifics.url && !cmsSection.specifics.cta ? (
        <Link className="group" href={cmsSection.specifics.url}>
          {renderContent()}
        </Link>
      ) : (
        renderContent()
      )}
    </SectionLayout>
  );
};

export default ImageTextSectionComponent;
